// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
// Style imports:
// import './Login.scss';
// Component imports:
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
// Action imports:
import { requestLogin, setUsernameAndPassword } from '../../redux-store/actions/loginActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';

type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps;

const Login: React.FC<Props> = ({
    username,
    password,
    loginErrorMessage,
    isSessionExpired,
    requestLogin,
    setUsernameAndPassword,
    accessTokenV2
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const usernameControlRef = useRef<HTMLInputElement | null>(null);
    const passwordControlRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        document.title = 'Login | KODIAK Sigti';
    }, []);

    useEffect(() => {
        if (accessTokenV2) {
            const from = location.state?.from;
            const isValidFrom = from && from.pathname && from.pathname !== '/login';
            const redirectPath = isValidFrom
                ? from.pathname + (from.search || '')
                : '/shares';
            navigate(redirectPath, { replace: true });
        }
    }, [accessTokenV2, location, navigate]);

    useEffect(() => {
        // Login through URL.
        const { search } = window.location;
        const query = new URLSearchParams(search);
        const queryUsername = query.get('username');
        const queryPassword = query.get('password');
        if (queryUsername !== null && queryPassword !== null) {
            requestLogin(username, password);
        }
    // eslint-disable-next-line
    }, []);

    const displayLoginError = () => {
        if (loginErrorMessage !== null) {
            return (
                <div className='LoginErrorBox'>
                    <p className='errorMessage'>{loginErrorMessage}</p>
                </div>
            );
        }
    }

    const displayLoginExpired = () => {
        if (isSessionExpired) {
            return (
                <div className='LoginWarningBox'>
                    <p className='warningMessage'>Your session has expired, please log back in</p>
                </div>
            );
        }
    }

    return (
        <div className='LoginArea'>
            {displayLoginError()}
            {displayLoginExpired()}
            <div className='LoginWindow'>
                <h2 className='LoginTitle'>
                    Log in to KODIAK Sigti
                </h2>
                <Form
                    className='LoginForm'
                    onSubmit={async (e: React.FormEvent) => {
                        e.preventDefault();
                        await requestLogin(username, password);
                    }}
                >
                    <Form.Group className='LoginFormGroup'>
                        <Form.Label className='LoginFormLabel'>
                            Username:
                        </Form.Label>
                        <Form.Control
                            ref={usernameControlRef}
                            type='text'
                            className='LoginFormControl'
                            onChange={e => setUsernameAndPassword(e.target.value, password)}
                        />
                        <FontAwesomeIcon
                            className='LoginFormIcon'
                            icon={faUser}
                            onClick={() => {
                                usernameControlRef.current?.focus();
                            }}
                        />
                    </Form.Group>
                    <Form.Group className='LoginFormGroup'>
                        <Form.Label className='LoginFormLabel'>
                            Password:
                        </Form.Label>
                        <Form.Control
                            ref={passwordControlRef}
                            type={(showPassword) ? 'text' : 'password'}
                            autoComplete='on'
                            className='LoginFormControl'
                            onChange={e => setUsernameAndPassword(username, e.target.value)}
                        />
                        <FontAwesomeIcon
                            className={'LoginFormIcon' + ((showPassword) ? ' ShowPassword' : '')}
                            icon={faEyeSlash}
                            onClick={() => {
                                setShowPassword(!showPassword);
                                passwordControlRef.current?.focus();
                            }}
                        />
                    </Form.Group>
                    <div className='FormButtonWrapper'>
                        <Button
                            variant='dark'
                            type='submit'
                            className='LoginClickItem'
                        >
                            Log In
                        </Button>
                    </div>
                </Form>
                <div className='LinkWrapper'>
                    <a
                        target='_blank'
                        rel='noopener noreferrer'
                        href='https://kodiak.is/kodiak-sigti.html'
                        className='LoginClickItem'
                    >
                        New User
                    </a>
                    <a
                        target='_blank'
                        rel='noopener noreferrer'
                        href='https://users.keldan.is/reset_password'
                        className='LoginClickItem'
                    >
                        Forgot password?
                    </a>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps({ loginReducer }: RootState) {
    const { username, password, loginErrorMessage, isSessionExpired, accessTokenV2 } = loginReducer;
    return {
        username,
        password,
        loginErrorMessage,
        isSessionExpired,
        accessTokenV2
    }
}

const connector = connect(mapStateToProps, { requestLogin, setUsernameAndPassword });
export default connector(Login);